/* ==========================================================================
   MRPIECE — Mix & Match filters.

   Category, gender, brand, colour, size, price and sort for the builder rails.
   Kept in its own file so assets/mp-mixmatch.js stays about picking two pieces
   and this stays about narrowing what it shows.

   Everything here is real Shopify filtering, done on the server:

     category / gender / brand / colour / size  ->  /collections/<handle>/<tag>+<tag>
     price                                      ->  ?filter.v.price.gte= &filter.v.price.lte=   (currency units, NOT cents)
     sort                                       ->  ?sort_by=

   All verified against this store on 25–26 Aug 2026. men-caps returns 241
   products; brand-nike returns 4; color-black returns 90; price under 800
   returns 109; black under 800 returns 38; size-42 + brand-adidas on slides
   returns 14.

   CATEGORY is the `type:` tag — type:caps, type:slides, type:t-shirts. It is
   what makes browsing the whole Mix & Match collection workable: a shopper can
   open every eligible piece and narrow it to caps themselves, instead of being
   limited to the category chips a merchant happened to build.

   GENDER is the `gender:` tag, shown as Male / Female / Unisex and carrying a
   colour so it reads at a glance: blue for male, pink for female, and the two
   together for unisex. It narrows within whichever side the Men / Women toggle
   has opened — inside the men's collection it separates strictly-men's pieces
   from unisex ones.

   The one rule that shapes this file: a tag Shopify does not recognise is
   SILENTLY IGNORED and the shopper gets the whole collection back, believing
   they filtered. So no option here is ever invented — every category, gender,
   brand, colour and size offered is read from the tags the rail reports for
   that collection. Nothing is hard-coded. The LABELS and SWATCH maps below
   only re-case or colour a label for display; the handle sent to Shopify is
   always the rail's own.

   The contract with mp-mixmatch.js is two data attributes and two events:
     write  data-mm-filter-path   on the step, e.g. "/type-caps+gender-unisex"
     write  data-mm-filter-params on the step, e.g. "&sort_by=price-ascending"
     fire   mm:reload             on the step to re-fetch
     hear   mm:rail               from the step, detail.meta = what can be filtered
   ========================================================================== */
(function () {
  'use strict';
  if (window.__mmFiltersLoaded) return;
  window.__mmFiltersLoaded = true;

  // Every tag-based dimension, in the order a shopper meets them. Category
  // leads because it answers "what am I even looking at"; gender follows
  // because it is the next thing most people narrow by.
  var TAG_GROUPS = ['type', 'gender', 'brand', 'color', 'size'];
  var META_KEY = { type: 'types', gender: 'genders', brand: 'brands', color: 'colors', size: 'sizes' };

  /* ---------- helpers ---------- */

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null) return '0';
    var parts = (cents / 100.0).toFixed(precision).split('.');
    return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents, format) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = (format || '${{amount}}').match(placeholder);
    if (!match) return format;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return format.replace(placeholder, value);
  }

  // Display only. The generic prettifier turns every hyphen into a space,
  // which is right for polo-shirts and wrong for t-shirts, so the handful of
  // names that need their own casing are listed. An unlisted type still gets a
  // sensible label; it just falls through to the generic rule.
  var TYPE_LABELS = {
    't-shirts': 'T-Shirts',
    'polo-shirts': 'Polo Shirts',
    'swim-shorts': 'Swim Shorts',
    'bucket-hats': 'Bucket Hats',
    'shoulder-bags': 'Shoulder Bags',
    'cross-bags': 'Crossbody Bags',
    'card-holders': 'Card Holders'
  };

  // The tag says men / women; the shopper is shown Male / Female.
  var GENDER_LABELS = { men: 'Male', women: 'Female', unisex: 'Unisex', kids: 'Kids' };

  // Blue for male, pink for female, both for unisex. Both hexes are already in
  // this store's own palette — the cobalt from the Offers Hub and the pink
  // from the For Her row — so the filter sits inside the brand rather than
  // beside it.
  var GENDER_SWATCH = {
    men: '#0B3FF0',
    women: '#EF2B8D',
    unisex: 'linear-gradient(135deg, #0B3FF0 0%, #EF2B8D 100%)',
    kids: 'linear-gradient(135deg, #7B9BFF 0%, #F7A8CD 100%)'
  };

  function genderSwatch(value) {
    return GENDER_SWATCH[String(value).toLowerCase()] || 'transparent';
  }

  // 'brand:louis-vuitton' -> 'Louis Vuitton'; sizes keep their own casing.
  function pretty(raw, group) {
    var value = raw.indexOf(':') > -1 ? raw.slice(raw.indexOf(':') + 1) : raw;
    var key = value.toLowerCase();
    if (group === 'size') return value.toUpperCase();
    if (group === 'gender' && GENDER_LABELS[key]) return GENDER_LABELS[key];
    if (group === 'type' && TYPE_LABELS[key]) return TYPE_LABELS[key];
    return value.split('-').map(function (w) {
      return w ? w.charAt(0).toUpperCase() + w.slice(1) : w;
    }).join(' ');
  }

  // Swatches for the colour names this catalogue actually uses. Anything not
  // listed falls back to a neutral chip rather than guessing a hex wrong.
  var SWATCH = {
    black: '#111111', white: '#ffffff', grey: '#9b9b9b', gray: '#9b9b9b', silver: '#c9c9c9',
    beige: '#e4d5bd', cream: '#f3ead7', tan: '#c69c6c', brown: '#6b4a2f', camel: '#c19a6b',
    red: '#c8102e', burgundy: '#6d1f2e', maroon: '#6d1f2e', pink: '#f0a3bd', rose: '#e08fa4',
    orange: '#e2712c', peach: '#f4b183', yellow: '#f0c33c', gold: '#c9a227',
    green: '#2f7a4f', olive: '#6b7040', mint: '#a9dcc0', teal: '#1f7a80',
    blue: '#2b5fd9', navy: '#1c2b52', denim: '#3f5f8f', purple: '#6b3fa0', lilac: '#c3a8e0',
    khaki: '#9a8b6a', camo: '#5b6b45', nude: '#e3c4ab', ivory: '#f7f2e7'
  };

  // A dot, sized inline so this file needs nothing new from the stylesheet.
  function dot(background, size) {
    var px = size || 10;
    return '<span aria-hidden="true" style="display:inline-block;flex:none;width:' + px + 'px;height:' + px +
      'px;border-radius:50%;background:' + background + ';box-shadow:inset 0 0 0 1px rgba(0,0,0,.12);' +
      'margin-inline-end:7px;vertical-align:middle"></span>';
  }

  var SIZE_ORDER = ['xxs', 'xs', 's', 'm', 'l', 'xl', '2xl', '3xl', '4xl', '5xl', 'one size', 'os'];
  var GENDER_ORDER = ['men', 'women', 'unisex', 'kids'];

  function sortSizes(a, b) {
    var av = a.value.toLowerCase(), bv = b.value.toLowerCase();
    var an = parseFloat(av), bn = parseFloat(bv);
    var aNum = !isNaN(an) && /^[\d.]+$/.test(av), bNum = !isNaN(bn) && /^[\d.]+$/.test(bv);
    if (aNum && bNum) return an - bn;
    if (aNum) return -1;
    if (bNum) return 1;
    var ai = SIZE_ORDER.indexOf(av), bi = SIZE_ORDER.indexOf(bv);
    if (ai > -1 && bi > -1) return ai - bi;
    if (ai > -1) return -1;
    if (bi > -1) return 1;
    return av.localeCompare(bv);
  }

  // Male, Female, Unisex reads better than the alphabetical Female, Male.
  function sortGenders(a, b) {
    var ai = GENDER_ORDER.indexOf(a.value.toLowerCase());
    var bi = GENDER_ORDER.indexOf(b.value.toLowerCase());
    if (ai > -1 && bi > -1) return ai - bi;
    if (ai > -1) return -1;
    if (bi > -1) return 1;
    return a.label.localeCompare(b.label);
  }

  // The rail sends "raw|handle" pairs so the handle is always Shopify's own,
  // never one this file guessed at.
  function parseTags(list, group) {
    return (list || []).map(function (entry) {
      var bits = String(entry).split('|');
      if (bits.length < 2) return null;
      return { raw: bits[0], handle: bits[1], label: pretty(bits[0], group), value: bits[0].split(':').pop() };
    }).filter(Boolean);
  }

  var SORTS = [
    { v: 'best-selling', label: 'Featured' },
    { v: 'price-ascending', label: 'Price: low to high' },
    { v: 'price-descending', label: 'Price: high to low' },
    { v: 'created-descending', label: 'Newest first' }
  ];

  /* ---------- one filter bar ---------- */

  function initBar(host) {
    if (host.dataset.ready === '1') return;
    host.dataset.ready = '1';

    var step = host.closest('[data-mm-step]');
    if (!step) return;
    var root = host.closest('[data-mm-root]');
    var moneyFormat = (root && root.getAttribute('data-money-format')) || '${{amount}}';

    var bands = ((root && root.getAttribute('data-price-bands')) || '1000,2000,4000')
      .split(',').map(function (n) { return parseInt(String(n).trim(), 10); })
      .filter(function (n) { return !isNaN(n) && n > 0; })
      .sort(function (a, b) { return a - b; });

    var state = { handle: null, tags: {}, price: null, sort: 'best-selling' };
    var meta = null;

    host.innerHTML =
      '<div class="mmf">' +
        '<div class="mmf__row" role="group" aria-label="Filter and sort"></div>' +
        '<div class="mmf__chips"></div>' +
      '</div>';
    var row = host.querySelector('.mmf__row');
    var chips = host.querySelector('.mmf__chips');

    // Bands are always whole amounts, so the trailing decimals are noise:
    // "Under LE 1,000" reads like a price, "Under LE 1,000.00" reads like a
    // receipt.
    function band(units) {
      return formatMoney(units * 100, moneyFormat).replace(/[.,]00(?=\D*$)/, '');
    }

    function priceOptions() {
      var out = [];
      bands.forEach(function (b, i) {
        if (i === 0) out.push({ key: 'p0', label: 'Under ' + band(b), gte: null, lte: b });
        else out.push({ key: 'p' + i, label: band(bands[i - 1]) + ' – ' + band(b), gte: bands[i - 1], lte: b });
      });
      if (bands.length) out.push({ key: 'p' + bands.length, label: 'Over ' + band(bands[bands.length - 1]), gte: bands[bands.length - 1], lte: null });
      return out;
    }

    /* ---- url ---- */

    function apply() {
      var path = [];
      TAG_GROUPS.forEach(function (g) {
        Object.keys(state.tags[g] || {}).forEach(function (h) { if (state.tags[g][h]) path.push(h); });
      });
      step.setAttribute('data-mm-filter-path', path.length ? '/' + path.join('+') : '');

      var params = '&sort_by=' + encodeURIComponent(state.sort);
      if (state.price) {
        if (state.price.gte != null) params += '&filter.v.price.gte=' + state.price.gte;
        if (state.price.lte != null) params += '&filter.v.price.lte=' + state.price.lte;
      }
      step.setAttribute('data-mm-filter-params', params);
      step.dispatchEvent(new CustomEvent('mm:reload'));
      track();
    }

    function track() {
      var active = [];
      TAG_GROUPS.forEach(function (g) {
        Object.keys(state.tags[g] || {}).forEach(function (h) { if (state.tags[g][h]) active.push(h); });
      });
      if (state.price) active.push('price');
      if (state.sort !== 'best-selling') active.push('sort:' + state.sort);
      if (!active.length) return;
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'offer_filter_apply', filters: active, category: state.handle });
      } catch (e) {}
      try { document.dispatchEvent(new CustomEvent('mrpiece:offer_filter_apply', { detail: { filters: active, category: state.handle } })); } catch (e) {}
    }

    /* ---- render ---- */

    function count(group) {
      return Object.keys(state.tags[group] || {}).filter(function (h) { return state.tags[group][h]; }).length;
    }

    function build() {
      if (!meta) return;
      var groups = [
        { key: 'type', label: 'Category', items: parseTags(meta.types, 'type'), min: 2 },
        { key: 'gender', label: 'Gender', items: parseTags(meta.genders, 'gender'), min: 2, gdot: true },
        { key: 'brand', label: 'Brand', items: parseTags(meta.brands, 'brand'), search: true },
        { key: 'color', label: 'Colour', items: parseTags(meta.colors, 'color'), swatch: true },
        { key: 'size', label: 'Size', items: parseTags(meta.sizes, 'size') }
      ];

      var html = '';
      groups.forEach(function (g) {
        // A Category button offering one category is a button that does
        // nothing: inside "Caps" everything is already a cap. Same for a
        // gender button in a rail that only holds one gender.
        if (g.items.length < (g.min || 1)) return;
        if (g.key === 'size') g.items.sort(sortSizes);
        else if (g.key === 'gender') g.items.sort(sortGenders);
        else g.items.sort(function (a, b) { return a.label.localeCompare(b.label); });
        var n = count(g.key);
        html += '<div class="mmf__group">' +
          '<button type="button" class="mmf__btn" data-mmf-open="' + g.key + '" aria-expanded="false">' +
            g.label + (n ? '<span class="mmf__n">' + n + '</span>' : '') +
            '<svg class="mmf__caret" viewBox="0 0 10 6" width="10" height="6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '</button>' +
          '<div class="mmf__panel" data-mmf-panel="' + g.key + '" hidden>' +
            (g.search ? '<input type="search" class="mmf__search" placeholder="Search ' + g.label.toLowerCase() + '" aria-label="Search ' + g.label.toLowerCase() + '">' : '') +
            '<div class="mmf__opts' + (g.swatch ? ' mmf__opts--swatch' : '') + '">' +
              g.items.map(function (it) {
                var on = !!(state.tags[g.key] && state.tags[g.key][it.handle]);
                var mark = '';
                if (g.swatch) {
                  mark = '<span class="mmf__swatch" style="background:' + (SWATCH[it.value.toLowerCase()] || 'transparent') + '"></span>';
                } else if (g.gdot) {
                  mark = dot(genderSwatch(it.value), 11);
                }
                return '<label class="mmf__opt' + (g.swatch && !SWATCH[it.value.toLowerCase()] ? ' is-unknown' : '') + '">' +
                  '<input type="checkbox" data-mmf-tag="' + it.handle + '" data-mmf-group="' + g.key + '"' + (on ? ' checked' : '') + '>' +
                  mark + '<span>' + it.label + '</span></label>';
              }).join('') +
            '</div>' +
          '</div></div>';
      });

      var po = priceOptions();
      if (po.length) {
        html += '<div class="mmf__group">' +
          '<button type="button" class="mmf__btn" data-mmf-open="price" aria-expanded="false">Price' +
            (state.price ? '<span class="mmf__n">1</span>' : '') +
            '<svg class="mmf__caret" viewBox="0 0 10 6" width="10" height="6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '</button>' +
          '<div class="mmf__panel" data-mmf-panel="price" hidden><div class="mmf__opts">' +
            '<label class="mmf__opt"><input type="radio" name="mmf-price-' + (step.getAttribute("data-mm-step") || "1") + '" data-mmf-price="" ' + (state.price ? '' : 'checked') + '><span>Any price</span></label>' +
            po.map(function (o) {
              var on = state.price && state.price.key === o.key;
              return '<label class="mmf__opt"><input type="radio" name="mmf-price-' + (step.getAttribute("data-mm-step") || "1") + '" data-mmf-price="' + o.key + '"' + (on ? ' checked' : '') + '><span>' + o.label + '</span></label>';
            }).join('') +
          '</div></div></div>';
      }

      html += '<div class="mmf__group">' +
        '<button type="button" class="mmf__btn" data-mmf-open="sort" aria-expanded="false">' +
          (SORTS.filter(function (s) { return s.v === state.sort; })[0] || SORTS[0]).label +
          '<svg class="mmf__caret" viewBox="0 0 10 6" width="10" height="6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="mmf__panel" data-mmf-panel="sort" hidden><div class="mmf__opts">' +
          SORTS.map(function (s) {
            return '<label class="mmf__opt"><input type="radio" name="mmf-sort-' + (step.getAttribute("data-mm-step") || "1") + '" data-mmf-sort="' + s.v + '"' + (state.sort === s.v ? ' checked' : '') + '><span>' + s.label + '</span></label>';
          }).join('') +
        '</div></div></div>';

      html += '<span class="mmf__count"></span>';
      html += '<button type="button" class="mmf__clear" hidden>Clear</button>';
      html += '<a class="mmf__all" href="#" hidden></a>';

      row.innerHTML = html;
      paintSummary();
      host.hidden = false;
    }

    function activeList() {
      var out = [];
      TAG_GROUPS.forEach(function (g) {
        Object.keys(state.tags[g] || {}).forEach(function (h) {
          if (!state.tags[g][h]) return;
          var item = parseTags(meta[META_KEY[g]], g).filter(function (i) { return i.handle === h; })[0];
          out.push({ group: g, handle: h, label: item ? item.label : h, value: item ? item.value : '' });
        });
      });
      if (state.price) out.push({ group: 'price', handle: state.price.key, label: state.price.label, value: '' });
      return out;
    }

    function paintSummary() {
      var active = activeList();
      chips.innerHTML = active.map(function (a) {
        var mark = a.group === 'gender' ? dot(genderSwatch(a.value), 8) : '';
        return '<button type="button" class="mmf__chip" data-mmf-remove="' + a.group + '" data-mmf-value="' + a.handle + '">' +
          mark + a.label + '<span aria-hidden="true">&times;</span></button>';
      }).join('');

      var clear = row.querySelector('.mmf__clear');
      if (clear) clear.hidden = active.length === 0 && state.sort === 'best-selling';

      var countEl = row.querySelector('.mmf__count');
      if (countEl && meta) countEl.textContent = meta.total === 1 ? '1 piece' : meta.total + ' pieces';

      var all = row.querySelector('.mmf__all');
      if (all && meta) {
        var path = step.getAttribute('data-mm-filter-path') || '';
        var params = (step.getAttribute('data-mm-filter-params') || '').replace(/^&/, '');
        all.setAttribute('href', '/collections/' + meta.handle + path + (params ? '?' + params : ''));
        all.textContent = 'View all ' + meta.total;
        all.hidden = !(meta.more && meta.total > 12);
      }
    }

    function closeAll(except) {
      Array.prototype.forEach.call(host.querySelectorAll('[data-mmf-panel]'), function (p) {
        if (p === except) return;
        p.hidden = true;
        var btn = host.querySelector('[data-mmf-open="' + p.getAttribute('data-mmf-panel') + '"]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    }

    /* ---- events ---- */

    host.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-mmf-open]');
      if (opener) {
        var key = opener.getAttribute('data-mmf-open');
        var panel = host.querySelector('[data-mmf-panel="' + key + '"]');
        var open = panel.hidden;
        closeAll(panel);
        panel.hidden = !open;
        opener.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) { var s = panel.querySelector('.mmf__search'); if (s) s.focus(); }
        return;
      }

      var clear = event.target.closest('.mmf__clear');
      if (clear) {
        state.tags = {}; state.price = null; state.sort = 'best-selling';
        build(); apply();
        return;
      }

      var chip = event.target.closest('[data-mmf-remove]');
      if (chip) {
        var g = chip.getAttribute('data-mmf-remove');
        var v = chip.getAttribute('data-mmf-value');
        if (g === 'price') state.price = null;
        else if (state.tags[g]) delete state.tags[g][v];
        build(); apply();
      }
    });

    host.addEventListener('change', function (event) {
      var t = event.target;

      if (t.hasAttribute && t.hasAttribute('data-mmf-tag')) {
        var g = t.getAttribute('data-mmf-group');
        var h = t.getAttribute('data-mmf-tag');
        state.tags[g] = state.tags[g] || {};
        if (t.checked) state.tags[g][h] = true; else delete state.tags[g][h];
        paintButtonCounts();
        paintSummary();
        apply();
        return;
      }

      if (t.hasAttribute && t.hasAttribute('data-mmf-price')) {
        var key = t.getAttribute('data-mmf-price');
        state.price = key ? priceOptions().filter(function (o) { return o.key === key; })[0] : null;
        paintButtonCounts();
        paintSummary();
        apply();
        return;
      }

      if (t.hasAttribute && t.hasAttribute('data-mmf-sort')) {
        state.sort = t.getAttribute('data-mmf-sort');
        var btn = host.querySelector('[data-mmf-open="sort"]');
        if (btn) btn.childNodes[0].nodeValue = (SORTS.filter(function (s) { return s.v === state.sort; })[0] || SORTS[0]).label;
        closeAll();
        paintSummary();
        apply();
      }
    });

    function paintButtonCounts() {
      TAG_GROUPS.forEach(function (g) {
        var btn = host.querySelector('[data-mmf-open="' + g + '"]');
        if (!btn) return;
        var badge = btn.querySelector('.mmf__n');
        var n = count(g);
        if (n && !badge) { badge = document.createElement('span'); badge.className = 'mmf__n'; btn.insertBefore(badge, btn.querySelector('.mmf__caret')); }
        if (badge) { badge.textContent = n; badge.hidden = !n; }
      });
      var pb = host.querySelector('[data-mmf-open="price"]');
      if (pb) {
        var pbadge = pb.querySelector('.mmf__n');
        if (state.price && !pbadge) { pbadge = document.createElement('span'); pbadge.className = 'mmf__n'; pb.insertBefore(pbadge, pb.querySelector('.mmf__caret')); }
        if (pbadge) { pbadge.textContent = '1'; pbadge.hidden = !state.price; }
      }
    }

    host.addEventListener('input', function (event) {
      if (!event.target.classList || !event.target.classList.contains('mmf__search')) return;
      var q = event.target.value.trim().toLowerCase();
      var opts = event.target.parentElement.querySelectorAll('.mmf__opt');
      Array.prototype.forEach.call(opts, function (o) {
        o.hidden = q !== '' && o.textContent.toLowerCase().indexOf(q) === -1;
      });
    });

    document.addEventListener('click', function (event) {
      if (!host.contains(event.target)) closeAll();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAll();
    });

    // The rail reports what this collection can be filtered by, every time it
    // loads. A new category means new brands and sizes, so the tag choices are
    // dropped; sort and price are about the shopper, not the category, so they
    // survive.
    step.addEventListener('mm:rail', function (event) {
      meta = event.detail && event.detail.meta;
      if (!meta) { host.hidden = true; return; }
      if (meta.handle !== state.handle) {
        state.handle = meta.handle;
        state.tags = {};
      }
      build();
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-mm-filters]'), initBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
